import { createDefaultPlayground } from '@/lib/playground-theme';
import { parsePlayground } from '@/lib/playground-storage';
import { generateHarmonyPalette, isValidHex, normalizeHex } from '@/lib/studio-color';
import { isRecord, readLocalJson, writeLocalJson, type StoredRead } from '@/lib/persist';
import { paletteSize, MAX_SAVED_PALETTES } from '@/lib/palette-workflow';
import type {
  GradientInterpolation,
  GradientStudioState,
  GradientType,
  HarmonyId,
  PaletteComposerState,
  PaletteSize,
  StudioState,
  StudioToolId,
  SavedPalette,
} from '@/types/studio';

export const STUDIO_STORAGE_KEY = 'color-studio.workbench.v1';
export type { StoredRead };

const HARMONIES: HarmonyId[] = [
  'analogous',
  'complementary',
  'split-complementary',
  'triadic',
  'monochromatic',
  'tetradic',
  'square',
];
const TOOLS: StudioToolId[] = [
  'palette',
  'gradient',
  'scale',
  'contrast',
  'playground',
  'image',
  'library',
];
const GRADIENT_TYPES: GradientType[] = ['linear', 'radial', 'conic'];
const INTERPOLATIONS: GradientInterpolation[] = ['srgb', 'oklab', 'oklch'];

const finite = (value: unknown, fallback: number, min: number, max: number): number =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(Math.max(value, min), max)
    : fallback;

const known = <T extends string>(value: unknown, options: T[], fallback: T): T =>
  typeof value === 'string' && options.includes(value as T) ? (value as T) : fallback;

export const createDefaultStudioState = (): StudioState => {
  const palette: Omit<PaletteComposerState, 'colors' | 'locked'> = {
    seed: '#6D5DFC',
    harmony: 'split-complementary',
    count: 5,
    chroma: 0.17,
    lightness: 0.68,
  };

  return {
    version: 1,
    name: 'Untitled palette',
    activeTool: 'palette',
    palette: {
      ...palette,
      colors: generateHarmonyPalette(palette),
      locked: Array.from({ length: palette.count }, () => false),
    },
    gradient: {
      type: 'linear',
      angle: 128,
      interpolation: 'oklab',
      selectedStopId: 'stop-2',
      stops: [
        { id: 'stop-1', color: '#FF7A59', position: 0 },
        { id: 'stop-2', color: '#6D5DFC', position: 48 },
        { id: 'stop-3', color: '#0FD6B3', position: 100 },
      ],
    },
    contrast: {
      foreground: '#F7F4EE',
      background: '#25212D',
    },
    mixer: {
      space: 'srgb',
      start: '#FF7A59',
      end: '#6D5DFC',
      amount: 50,
    },
    playground: createDefaultPlayground(),
    library: [],
  };
};

const parsePalette = (value: unknown, defaults: PaletteComposerState): PaletteComposerState => {
  if (!isRecord(value)) return defaults;
  const count: PaletteSize = paletteSize(finite(value.count, defaults.count, 2, 12));
  const seed =
    typeof value.seed === 'string' && isValidHex(value.seed)
      ? normalizeHex(value.seed)
      : defaults.seed;
  const harmony = known(value.harmony, HARMONIES, defaults.harmony);
  const chroma = finite(value.chroma, defaults.chroma, 0.04, 0.29);
  const lightness = finite(value.lightness, defaults.lightness, 0.35, 0.85);
  const colors = Array.isArray(value.colors)
    ? value.colors
        .filter((color): color is string => typeof color === 'string' && isValidHex(color))
        .map((color) => normalizeHex(color))
    : [];

  const base = { seed, harmony, count, chroma, lightness };
  return {
    ...base,
    colors: colors.length === count ? colors : generateHarmonyPalette(base),
    locked: Array.from(
      { length: count },
      (_, index) => Array.isArray(value.locked) && value.locked[index] === true,
    ),
  };
};

const parseLibrary = (value: unknown): SavedPalette[] => {
  if (!Array.isArray(value)) return [];
  const ids = new Set<string>();
  return value.slice(0, MAX_SAVED_PALETTES).flatMap((entry) => {
    if (
      !isRecord(entry) ||
      typeof entry.id !== 'string' ||
      !entry.id ||
      ids.has(entry.id) ||
      typeof entry.name !== 'string' ||
      !Array.isArray(entry.colors) ||
      entry.colors.length < 2 ||
      entry.colors.length > 12 ||
      !entry.colors.every((c) => typeof c === 'string' && isValidHex(c))
    )
      return [];
    ids.add(entry.id);
    return [
      {
        id: entry.id.slice(0, 120),
        name: entry.name.trim().slice(0, 80) || 'Untitled palette',
        colors: entry.colors.map((c) => normalizeHex(c as string)),
        favorite: entry.favorite === true,
        createdAt: typeof entry.createdAt === 'string' ? entry.createdAt.slice(0, 40) : '',
      },
    ];
  });
};

const parseGradient = (value: unknown, defaults: GradientStudioState): GradientStudioState => {
  if (!isRecord(value)) return defaults;
  const usedIds = new Set<string>();
  const parsedStops = Array.isArray(value.stops)
    ? value.stops
        .map((stop, index) => {
          if (!isRecord(stop) || typeof stop.color !== 'string' || !isValidHex(stop.color))
            return null;

          const preferredId = typeof stop.id === 'string' && stop.id ? stop.id : 'stored-stop';
          let id = preferredId;
          let suffix = index + 1;
          while (usedIds.has(id)) {
            id = `${preferredId}-${suffix}`;
            suffix += 1;
          }
          usedIds.add(id);

          return {
            id,
            color: normalizeHex(stop.color),
            position: finite(stop.position, index * 50, 0, 100),
          };
        })
        .filter((stop): stop is NonNullable<typeof stop> => stop !== null)
        .slice(0, 8)
    : [];
  const stops = parsedStops.length >= 2 ? parsedStops : defaults.stops;
  const selectedStopId =
    typeof value.selectedStopId === 'string' &&
    stops.some((stop) => stop.id === value.selectedStopId)
      ? value.selectedStopId
      : (stops[0]?.id ?? defaults.selectedStopId);

  return {
    type: known(value.type, GRADIENT_TYPES, defaults.type),
    angle: finite(value.angle, defaults.angle, 0, 360),
    interpolation: known(value.interpolation, INTERPOLATIONS, defaults.interpolation),
    stops,
    selectedStopId,
  };
};

export const parseStudioState = (value: unknown): StoredRead<StudioState> => {
  const defaults = createDefaultStudioState();
  if (value === null) return { value: defaults, discarded: false };
  try {
    if (!isRecord(value)) return { value: defaults, discarded: true };

    const contrast = isRecord(value.contrast) ? value.contrast : {};
    const mixer = isRecord(value.mixer) ? value.mixer : {};

    return {
      value: {
        version: 1,
        name:
          typeof value.name === 'string'
            ? value.name.trim().slice(0, 80) || 'Untitled palette'
            : defaults.name,
        activeTool: known(value.activeTool, TOOLS, defaults.activeTool),
        palette: parsePalette(value.palette, defaults.palette),
        gradient: parseGradient(value.gradient, defaults.gradient),
        contrast: {
          foreground:
            typeof contrast.foreground === 'string' && isValidHex(contrast.foreground)
              ? normalizeHex(contrast.foreground)
              : defaults.contrast.foreground,
          background:
            typeof contrast.background === 'string' && isValidHex(contrast.background)
              ? normalizeHex(contrast.background)
              : defaults.contrast.background,
        },
        mixer: {
          space: known(mixer.space, INTERPOLATIONS, defaults.mixer.space),
          start:
            typeof mixer.start === 'string' && isValidHex(mixer.start)
              ? normalizeHex(mixer.start)
              : defaults.mixer.start,
          end:
            typeof mixer.end === 'string' && isValidHex(mixer.end)
              ? normalizeHex(mixer.end)
              : defaults.mixer.end,
          amount: finite(mixer.amount, defaults.mixer.amount, 0, 100),
        },
        playground: parsePlayground(value.playground),
        library: parseLibrary(value.library),
      },
      discarded: false,
    };
  } catch {
    return { value: defaults, discarded: true };
  }
};

export const readStoredStudioStateWithStatus = (): StoredRead<StudioState> => {
  const stored = readLocalJson(STUDIO_STORAGE_KEY);
  return stored.discarded
    ? { value: createDefaultStudioState(), discarded: true }
    : parseStudioState(stored.value);
};

export const readStoredStudioState = (): StudioState => readStoredStudioStateWithStatus().value;

export const writeStoredStudioState = (state: StudioState): boolean => {
  return writeLocalJson(STUDIO_STORAGE_KEY, state);
};
